import type { Attribution, LicenseCode, RegionManifest } from '../types';
import { activePacks, region } from './region';
import { t } from '../strings';

// Every bundled clip and photo is somebody's Creative Commons work, and most of
// those licenses require the credit to be visible — not merely present in a
// build artifact. The pipeline already emits the flat list (stage 06); this
// reads it and rolls it up into something a person can actually check.
//
// It is fetched on demand rather than bundled: at 120 species the list is ~170
// KB of JSON that only the Credits screen ever reads, and it grows linearly
// with the species list while the app shell should not. Offline, it falls back
// to the region manifest, which carries the same credits per asset.

/** One row of the pipeline's attributions.json. */
export interface AttributionEntry extends Attribution {
  /** eBird code of the species the asset belongs to. */
  code: string;
  kind: 'audio' | 'photo';
  /** Asset id, e.g. "xc680975" or "inat2713284". */
  id: string;
}

export interface Contributor {
  author: string;
  audio: number;
  photos: number;
  licenses: LicenseCode[];
  /** One representative asset page, so the name links somewhere real. */
  sourceUrl: string;
  /** Distinct species this person contributed to. */
  species: number;
}

export interface CreditsSummary {
  entries: number;
  audio: number;
  photos: number;
  contributors: Contributor[];
  /** License code → asset count, most-used first. */
  licenses: { license: LicenseCode; count: number }[];
}

/**
 * Keyed by region id, not a single slot.
 *
 * A single `cached` was correct while the app could only ever load one region.
 * It is a bug the moment a session can switch packs, which it now can from the
 * Home screen: the second pack's credits would come back as the first's, and
 * the credits screen is the one screen a licence actually requires to be right.
 */
const cached = new Map<string, Promise<AttributionEntry[]>>();

/** Fetch a pack's attribution list. Cached — it never changes at runtime. */
export function loadAttributions(regionId: string): Promise<AttributionEntry[]> {
  const hit = cached.get(regionId);
  if (hit) return hit;
  const pending = fetchAttributions(regionId);
  cached.set(regionId, pending);
  return pending;
}

/** Every active pack's credits, concatenated. */
export async function loadAllAttributions(
  regionIds: string[],
): Promise<AttributionEntry[]> {
  const lists = await Promise.all(regionIds.map(loadAttributions));
  return lists.flat();
}

async function fetchAttributions(regionId: string): Promise<AttributionEntry[]> {
  const url = `${import.meta.env.BASE_URL}data/regions/${regionId}/attributions.json`;
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(String(res.status));
    return (await res.json()) as AttributionEntry[];
  } catch {
    // Offline, or the file isn't deployed. attributions.json is deliberately
    // outside the service worker's precache — it is large and grows with the
    // species list — but every credit in it is also carried per-asset by the
    // region manifest, which *is* precached. Rebuilding from that is exact, so
    // the one screen a licence requires to be visible never goes dark.
    return fromManifest(
      activePacks().find((m) => m.id === regionId) ?? region(),
    );
  }
}

/**
 * The same rows attributions.json holds, recovered from the region manifest.
 * The pipeline emits that file from these very fields (stage 06), so the two
 * agree by construction — there is a test pinning that.
 */
export function fromManifest(m: RegionManifest): AttributionEntry[] {
  const rows: AttributionEntry[] = [];
  for (const sp of m.species) {
    for (const a of sp.audio) {
      rows.push({ code: sp.code, kind: 'audio', id: a.id, license: a.license,
        author: a.author, sourceUrl: a.sourceUrl, source: a.source });
    }
    for (const p of sp.photos) {
      rows.push({ code: sp.code, kind: 'photo', id: p.id, license: p.license,
        author: p.author, sourceUrl: p.sourceUrl, source: p.source });
    }
  }
  return rows;
}

/**
 * Roll the flat asset list up per contributor.
 *
 * Sorted by name rather than by contribution count: a credits roll is an
 * acknowledgement, not a leaderboard, and alphabetical is the order in which
 * someone can find themselves in it.
 */
export function summarize(entries: AttributionEntry[]): CreditsSummary {
  const byAuthor = new Map<string, Contributor & { codes: Set<string> }>();
  const byLicense = new Map<LicenseCode, number>();
  let audio = 0;
  let photos = 0;

  for (const e of entries) {
    if (e.kind === 'audio') audio++;
    else photos++;
    byLicense.set(e.license, (byLicense.get(e.license) ?? 0) + 1);

    let c = byAuthor.get(e.author);
    if (!c) {
      c = {
        author: e.author,
        audio: 0,
        photos: 0,
        licenses: [],
        sourceUrl: e.sourceUrl,
        species: 0,
        codes: new Set(),
      };
      byAuthor.set(e.author, c);
    }
    if (e.kind === 'audio') c.audio++;
    else c.photos++;
    if (!c.licenses.includes(e.license)) c.licenses.push(e.license);
    c.codes.add(e.code);
  }

  const contributors = [...byAuthor.values()]
    .map(({ codes, ...c }) => ({ ...c, species: codes.size }))
    // Numeric so "Bob 10" doesn't sort before "Bob 2", and case-insensitive so
    // usernames like "guyincognito" file among the names rather than after them.
    .sort((a, b) => a.author.localeCompare(b.author, undefined, { numeric: true, sensitivity: 'base' }));

  const licenses = [...byLicense.entries()]
    .map(([license, count]) => ({ license, count }))
    .sort((a, b) => b.count - a.count);

  return { entries: entries.length, audio, photos, contributors, licenses };
}

/** Human label for a source, for prose that shouldn't read like an enum. */
export function sourceLabel(source: Attribution['source']): string {
  return t(`common.source.${source}`);
}

/** The canonical page for a license code. */
export function licenseUrl(license: LicenseCode): string {
  if (license === 'CC0') return 'https://creativecommons.org/publicdomain/zero/1.0/';
  const slug = license.replace(/^CC-/, '').toLowerCase();
  return `https://creativecommons.org/licenses/${slug}/4.0/`;
}
